import { Extension } from "@tiptap/core"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    remark: {
      /**
       * Toggle a `# ` line-comment prefix on every paragraph the current
       * selection touches. Mixed selections always get commented (VS Code
       * semantics): if any line lacks `#`, all lines gain `# `; otherwise
       * all lines lose their leading `#` (and one optional trailing space).
       */
      toggleRemarks: () => ReturnType
    }
  }
}

interface ParagraphInfo {
  pos: number
  hasHash: boolean
  hasHashSpace: boolean
}

const RemarkExtension = Extension.create({
  name: "remark",

  addCommands() {
    return {
      toggleRemarks:
        () =>
        ({ state, dispatch }) => {
          const { from, to } = state.selection

          const paragraphs: ParagraphInfo[] = []
          state.doc.nodesBetween(from, to, (node: ProseMirrorNode, pos: number) => {
            if (node.type.name === "paragraph") {
              const text = node.textContent
              paragraphs.push({
                pos,
                hasHash: text.startsWith("#"),
                hasHashSpace: text.startsWith("# "),
              })
              return false
            }
            return true
          })

          if (paragraphs.length === 0) return false

          if (!dispatch) return true

          const allHash = paragraphs.every((p) => p.hasHash)
          const tr = state.tr

          for (let i = paragraphs.length - 1; i >= 0; i--) {
            const p = paragraphs[i]
            const start = p.pos + 1
            if (allHash) {
              const removeLen = p.hasHashSpace ? 2 : 1
              tr.delete(start, start + removeLen)
            } else {
              tr.insertText("# ", start)
            }
          }

          dispatch(tr)
          return true
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      "Mod-/": () => this.editor.commands.toggleRemarks(),
    }
  },
})

export default RemarkExtension
